import { Box, Button, Divider, VStack } from "@chakra-ui/react";
import { type TezosToolkit } from "@taquito/taquito";
import type { BatchWalletOperation } from "@taquito/taquito/dist/types/wallet/batch-operation";
import {
  ChainIds,
  type ImplicitAccount,
  type LedgerAccount,
  type MnemonicAccount,
  type Network,
  type SecretKeyAccount,
  type SignTransactionOptions,
  type SocialAccount,
  makeToolkit,
} from "@umami/chains";
import * as Auth from "@umami/social-auth";
import { useAsyncActionHandler, useGetSecretKey, useSelectedNetwork } from "@umami/state";
import { useCustomToast } from "@umami/utils";
import { type TransactionResponse } from "ethers";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";

import { usePasskey } from "../../hooks/usePasskey";

import { PasswordInput } from "@/components/PasswordInput";

export const SignButton = ({
  signer,
  onSubmit,
  isLoading: externalIsLoading,
  isDisabled,
  text = "Confirm transaction",
}: {
  onSubmit: (
    tezosToolkit: TezosToolkit,
    options: SignTransactionOptions
  ) => Promise<BatchWalletOperation | TransactionResponse | void>;
  signer: ImplicitAccount;
  isLoading?: boolean;
  isDisabled?: boolean;
  text?: string; // TODO: after FillStep migration change to the header value from SignPage
  network?: Network;
}) => {
  const { authenticate, isPasskeyFlow, isPasskeyUser, isLoading: isPasskeyLoading } = usePasskey();

  const chainId = useWatch({ name: "chainId" }) ?? ChainIds.TEZOS;
  const form = useForm<{ password: string }>({ mode: "onBlur", defaultValues: { password: "" } });
  const {
    handleSubmit,
    formState: { isValid: isPasswordValid },
  } = form;
  const network = useSelectedNetwork();

  const {
    formState: { isValid: isOuterFormValid },
  } = useFormContext();

  const isButtonDisabled = isDisabled || !isPasswordValid || !isOuterFormValid;
  const isActionDisabled = isDisabled || !isOuterFormValid;

  const getSecretKey = useGetSecretKey();
  const toast = useCustomToast();
  const { isLoading: internalIsLoading, handleAsyncAction } = useAsyncActionHandler();
  const isLoading = internalIsLoading || externalIsLoading;
  const onPasskeySign = async () => {
    const { dek: password } = await authenticate();
    signer.type === "mnemonic"
      ? await onMnemonicSign({ password })
      : await onSecretKeySign({ password });
  };

  const onMnemonicSign = async ({ password }: { password: string }) =>
    handleAsyncAction(async () => {
      const { secretKey, mnemonic } = await getSecretKey(signer as MnemonicAccount, password);
      const toolkit = await makeToolkit({ type: "mnemonic", secretKey, network });

      return onSubmit(toolkit, {
        mnemonic,
        derivationPaths: (signer as MnemonicAccount).derivationPaths,
      });
    });

  const onSecretKeySign = async ({ password }: { password: string }) =>
    handleAsyncAction(async () => {
      const { secretKey } = await getSecretKey(signer as SecretKeyAccount, password);

      return onSubmit(await makeToolkit({ type: "secret_key", secretKey, network }), {
        secretKey,
      });
    });

  const onSocialSign = async () =>
    handleAsyncAction(async () => {
      const { secretKey } = await Auth.forIDP((signer as SocialAccount).idp).getCredentials();

      return onSubmit(await makeToolkit({ type: "social", secretKey, network }), {
        secretKey,
      });
    });

  const onLedgerSign = async () =>
    handleAsyncAction(
      async () => {
        toast({
          id: "ledger-sign-toast",
          description: "Please approve the operation on your Ledger",
          status: "info",
          duration: 60000,
          isClosable: true,
        });

        // We don't need to create tezosToolkit for non-tezos chains when signing with ledger
        const toolkit =
          chainId === ChainIds.TEZOS
            ? await makeToolkit({
                type: "ledger",
                account: signer as LedgerAccount,
                network,
              })
            : ({} as TezosToolkit);

        return onSubmit(toolkit, {
          isLedger: true,
          derivationPath: (signer as LedgerAccount).derivationPath,
        });
      },
      (error: any) => ({
        description: `${error.message} Please connect your ledger, open corresponding app and try submitting transaction again`,
        status: "error",
      })
    ).finally(() => toast.close("ledger-sign-toast"));

  switch (signer.type) {
    case "secret_key":
    case "mnemonic":
      return isPasskeyFlow && isPasskeyUser ? (
        <Button
          width="full"
          marginTop="24px"
          isDisabled={isActionDisabled}
          isLoading={isLoading || isPasskeyLoading}
          onClick={onPasskeySign}
          size="lg"
          variant="primary"
        >
          {text}
        </Button>
      ) : (
        <Box width="full">
          <FormProvider {...form}>
            <VStack alignItems="start" spacing="24px">
              <Divider />
              <PasswordInput data-testid="password" inputName="password" />
              <Button
                width="full"
                isDisabled={isButtonDisabled}
                isLoading={isLoading}
                onClick={handleSubmit(
                  signer.type === "mnemonic" ? onMnemonicSign : onSecretKeySign
                )}
                size="lg"
                type="submit"
                variant="primary"
              >
                {text}
              </Button>
            </VStack>
          </FormProvider>
        </Box>
      );
    case "social":
      return (
        <Button
          width="full"
          marginTop="24px"
          isDisabled={isActionDisabled}
          isLoading={isLoading}
          onClick={onSocialSign}
          size="lg"
          variant="primary"
        >
          {text}
        </Button>
      );
    case "ledger":
      return (
        <Button
          width="full"
          marginTop="24px"
          isDisabled={isActionDisabled}
          isLoading={isLoading}
          onClick={onLedgerSign}
          size="lg"
          variant="primary"
        >
          {text}
        </Button>
      );
  }
};
